import { DZ_COLOR_NEUTRAL_200 } from '@blk/daisy/core/styles';
import { TabData } from '@ddt-web/data-service';
import { ObjectUtil, StringUtil } from '@ddt-web/generic-utils';

import { ChartFactory } from '../factories';
import { TooltipDispatcher } from '../tooltips/dispatcher.tooltip';
import { ChartConfigHelper } from './config.helper';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';
import { ColumnChartName } from './enums/column-chart-name.enum';

declare const Highcharts;

const DEFAULT_PIE_CHART_CONFIG: any = {
	plotOptions: {
		pie: {
			dataLabels: {
				enabled: false
			},
			innerSize: '80%',
			startAngle: 0,
			endAngle: 360,
			center: ['50%', '50%']
		}
	},
	tooltip: {
		formatter: function (): string {
			return '<b>' + StringUtil.escapeHtml(this.point.series.name) + '</b>' + Highcharts.numberFormat(this.point.y, 2, '.', ',') + '%';
		}
	},
	title: {
		align: 'center',
		verticalAlign: 'middle',
		y: -15
	}
};

export class PieChartConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];
	private seriesData: Array<any> = [];
	private seriesTitle: string;

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: any,
		protected dataIndex: number = 0,
		protected dataBatch: number = 0,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: PieChartCustomAttributes,
		protected tooltipType?: string
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_PIE_CHART_CONFIG);
		if (this.data instanceof TabData) {
			this.seriesColors = this.configuration.colors || ChartFactory.getDefaultColorsForTabData(this.data, this.dataIndex, this.dataBatch);
			this.seriesData = ChartFactory.getDefaultPieSeriesForTabData(this.data, this.dataIndex, this.dataBatch);
			this.seriesTitle = ChartFactory.getDefaultPieTitleForTabData(this.data, this.dataIndex, this.dataBatch);
		}

		this.configuration.tooltip.formatter = new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter();
		this.updateDefaultConfiguration();

		if (this.customAttributes) {
			this.configuration = ChartConfigHelper.applyCustomAttributesToConfiguration(this.customAttributes, this.configuration);
		}
	}

	public getConfigurationForPDF(): any {
		return ObjectUtil.extend(this.configuration, {
			plotOptions: {
				pie: {
					dataLabels: {
						enabled: false
					},
					innerSize: '55%',
					startAngle: 0,
					endAngle: 360,
					center: ['50%', '50%']
				}
			},
			responsive: null,
			title: {
				text: null
			}
		});
	}

	public render(): void {
		switch (this.name) {
			case 'areas-to-explore':
				this.configuration.tooltip = {
					formatter: function (): string {
						return StringUtil.escapeHtml(this.point.series.name) + ' <b>' + StringUtil.escapeHtml(this.point.y) + '%';
					},
					backgroundColor: '#f9f9f9',
					borderColor: '#a5a5a5',
					borderRadius: 0,
					shadow: false,
					useHTML: true,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif'
					}
				};

				this.configuration.title.text = this.title ? this.title : null;
				this.renderDefaultChart();
				break;
			case 'exposure':
				this.configuration.tooltip.pointFormat =
					'<div class="asset-allocation-tooltip row text-12"><span class="col-15">{point.category}</span><span class="col-9 text-align-right">{point.formattedY}</span></div>';
				this.renderDefaultChart();
				break;
			case ColumnChartName.IqAnalyticsChart:
				this.configuration.tooltip = {
					formatter: function (): string {
						return `<div style="width: 120px"><b>% of time in ${StringUtil.escapeHtml(this.point.category)}</b> ${StringUtil.escapeHtml(this.point.formattedY)}</div>`;
					},
					backgroundColor: 'rgba(255, 255, 255, 0.9)',
					borderColor: DZ_COLOR_NEUTRAL_200,
					borderRadius: 0,
					shadow: false,
					useHTML: true,
					padding: 10,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif'
					}
				};

				this.configuration.title.text = this.title ? this.title : null;
				this.renderDefaultChart();
				break;
			default:
				this.renderDefaultChart();
				break;
		}
	}

	private updateDefaultConfiguration(): void {
		const donutMaxWidth = 200;
		const containerWidth = this.container.offsetWidth;
		const chartHeight = this.height ? this.height : containerWidth <= donutMaxWidth ? containerWidth : donutMaxWidth;
		const chartWidth = this.width ? this.width : containerWidth <= donutMaxWidth ? containerWidth : donutMaxWidth;
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				height: chartHeight,
				width: chartWidth
			},
			colors: this.seriesColors,
			responsive: {
				rules: [
					{
						condition: { maxWidth: 140 },
						chartOptions: {
							chart: {
								height: chartHeight + 60,
								spacingBottom: 60
							},
							plotOptions: {
								pie: {
									innerSize: '55%'
								}
							},
							title: {
								verticalAlign: 'bottom',
								widthAdjust: -20,
								y: 10,
								style: {
									fontSize: '12px',
									lineHeight: '16px'
								}
							}
						}
					},
					{
						condition: { maxWidth: 90 },
						chartOptions: {
							chart: {
								height: chartHeight + 90,
								spacingBottom: 90
							},
							plotOptions: {
								pie: {
									innerSize: '0%'
								}
							},
							title: {
								widthAdjust: -10
							}
						}
					}
				]
			},
			title: {
				text: this.hideTitle ? '' : this.seriesTitle,
				widthAdjust: this.width ? (this.width >= 175 ? -100 : -50) : containerWidth >= 175 ? -100 : -50,
				style: {
					fontSize: this.width ? (this.width >= 200 ? '14px' : '11px') : containerWidth >= 200 ? '14px' : '11px',
					lineHeight: this.width ? (this.width >= 200 ? '18px' : '13px') : containerWidth >= 200 ? '18px' : '13px'
				}
			},
			series: this.seriesData
		});
	}

	private renderDefaultChart(): void {
		Highcharts.chart(this.configuration);
	}
}

export interface PieChartCustomAttributes {
	// template interface for now in case someone need manual config on these chart
	[id: string]: any;
}
